<template>
  <div>
    <SidbearSettingsGroup
      active
      title="Navbar"
    >
      <div class="d-flex justify-content-between align-items-center mb-[12px]">
        <p class="text-capitalize mb-0">
          Menu Items
        </p>
      </div>

      <div
        class="d-flex align-items-center shadow-sm border rounded cursor-pinter font-[600] px-[13px] mb-3 h-[38px]"
        @click="dialog(true)"
      >
        <svg
          class="mr-[10px]"
          fill="none"
          height="12"
          viewBox="0 0 14 12"
          width="14"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.28033 0.21967C7.98744 -0.0732233 7.51256 -0.0732233 7.21967 0.21967C6.92678 0.512563 6.92678 0.987437 7.21967 1.28033L11.1893 5.25H1C0.585786 5.25 0.25 5.58579 0.25 6C0.25 6.41421 0.585786 6.75 1 6.75H11.1893L7.21967 10.7197C6.92678 11.0126 6.92678 11.4874 7.21967 11.7803C7.51256 12.0732 7.98744 12.0732 8.28033 11.7803L13.5303 6.53033C13.8232 6.23744 13.8232 5.76256 13.5303 5.46967L8.28033 0.21967Z"
            fill="#3D82EA"
          />
        </svg>

        <label class="text-[14px] text-[#44474A] mb-0 pb-0">Go to Site Navigation</label>
      </div>

      <background-customs :config="section.options.customize.style.background" />

      <div class="flex items-end gap-[10px]">
        <font-size-control
          :config="config"
          class="w-[80px]"
          label="Text size"
        />

        <alignment-items-control
          :config="config"
          label=""
        />
      </div>

      <font-family-and-weight-control
        :global-style-option-enable="false"
        :family="fontFamily"
        :weight="fontWeight"
        @family="family => fontFamily = family"
        @weight="weight => fontWeight = weight"
      />

      <!-- Active Styles -->
      <b-form-group label="Active link style">
        <v-select
          v-model="activeLinkStyle"
          :options="activeLinkStyleOptions"
          :reduce="option => option.value"
          :searchable="false"
          class="custom-select"
          left
        />
      </b-form-group>

      <b-form-group
        v-if="activeLinkStyle === 'background'"
        label="Active link shape"
      >
        <v-select
          v-model="shape"
          :options="shapeOptions"
          :reduce="option => option.key"
          :searchable="false"
          class="custom-select"
          left
        />
      </b-form-group>

      <b-form-group v-if="activeLinkStyle !== 'color'">
        <div class="flex items-center gap-[14px]">
          <color-picker
            :color="activeBackgroundColor"
            @input="event => activeBackgroundColor = `rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`"
          />
          <label class="mb-0 pb-0">Active Color</label>
        </div>
      </b-form-group>

      <b-form-group>
        <div class="flex items-center gap-[14px]">
          <text-color v-model="config.color" />
          <label class="mb-0 pb-0">Active Text Color</label>
        </div>
      </b-form-group>

      <b-form-group>
        <div class="flex items-center gap-[14px]">
          <text-color v-model="linksColor" />
          <label class="mb-0 pb-0">Text Link Color</label>
        </div>
      </b-form-group>

      <b-form-group>
        <div class="flex items-center gap-[14px]">
          <color-picker
            :color="dropdownColor"
            @input="event => dropdownColor = `rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`"
          />
          <label class="mb-0 pb-0">Dropdown Menu Color</label>
        </div>
      </b-form-group>

      <b-form-group>
        <div class="flex items-center gap-[14px]">
          <text-color v-model="dropdownTextColor" />
          <label class="mb-0 pb-0">Dropdown Text Color</label>
        </div>
      </b-form-group>

      <b-form-group>
        <multi-slider
          v-model="menuWidth"
          label="Menu Width"
          :default-type="1"
        />
      </b-form-group>

      <b-form-group
        label="Collapsed Menu Spacing"
        label-class="!pb-[3px]"
      >
        <b-row class="align-items-center pl-2">
          <b-col
            cols="8"
          >
            <vue-slider
              v-model="spacing"
              :interval="1"
              :max="20"
              :min="0"
              class="editor-slider-1"
              tooltip="none"
            />
          </b-col>
          <b-col>
            <linked-control-item
              v-model="spacing"
              :max="20"
              :min="0"
              class="d-block"
            />
          </b-col>
        </b-row>
      </b-form-group>

      <b-form-group label="Open icon">
        <div class="flex items-center gap-[10px] w-[150px]">
          <icon-select v-model="iconOpen" />
          <color-picker
            :color="iconColor"
            @input="event => iconColor = `rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`"
          />
        </div>
      </b-form-group>

      <b-form-group label="Close icon">
        <div class="flex items-center gap-[10px] w-[150px]">
          <icon-select v-model="iconClose" />
          <color-picker
            :color="closeIconColor"
            @input="event => closeIconColor = `rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`"
          />
        </div>
      </b-form-group>

      <b-form-group>
        <b-checkbox v-model="config.uppercase">
          All CAPS
        </b-checkbox>
      </b-form-group>

      <b-form-group>
        <b-checkbox v-model="showButton">
          Show button
        </b-checkbox>
      </b-form-group>

      <b-form-group>
        <b-checkbox v-model="bringToFront">
          bring to front / overlay page content
        </b-checkbox>
      </b-form-group>

      <b-form-group>
        <b-checkbox v-model="fullWidthMenu">
          Full Width Menu
        </b-checkbox>
      </b-form-group>

      <b-form-group>
        <b-checkbox v-model="wrapLinks">
          Wrap links to new line
        </b-checkbox>
      </b-form-group>
    </SidbearSettingsGroup>

    <SidbearSettingsGroup
      content-class="pb-0"
      title="Scroll Effect"
    >
      <navbar-scroller-customs :config="section.options.customize.style.navbarScroller" />
    </SidbearSettingsGroup>


    <SidbearSettingsGroup
      title="Hover Styles"
    >
      <!-- On Hover Style -->
      <b-form-group label="Hover Link Style">
        <v-select
          v-model="onHover"
          :options="onHoverOptions"
          :reduce="option => option.value"
          :searchable="false"
        />
      </b-form-group>
      <!-- End On Hover Style -->

      <b-form-group>
        <div class="flex items-center gap-[14px]">
          <text-color v-model="linkHoverColor" />
          <label class="mb-0 pb-0">Hover Text Color</label>
        </div>
      </b-form-group>

      <b-form-group>
        <div class="flex items-center gap-[14px]">
          <color-picker
            :color="linkHoverBackground"
            @input="event => linkHoverBackground = `rgba(${event.rgba.r}, ${event.rgba.g}, ${event.rgba.b}, ${event.rgba.a})`"
          />
          <label class="mb-0 pb-0">Hover Background Color</label>
        </div>
      </b-form-group>

      <b-form-group>
        <v-select
          v-model="linkHoverBackgroundShape"
          :options="shapeOptions"
          :reduce="option => option.key"
          :searchable="false"
          class="custom-select"
          left
        />
      </b-form-group>
    </SidbearSettingsGroup>

    <SidbearSettingsGroup
      title="Drop Shadow"
    >
      <shadow-customs :config="config" />
    </SidbearSettingsGroup>

    <SidbearSettingsGroup
      title="Border & Radius"
    >
      <border-customs
        :config="config"
        :default-border="[0,0,0,0]"
        :default-radius="[0,0,0,0]"
      />
    </SidbearSettingsGroup>

    <SidbearSettingsGroup
      title="Padding & Margins"
    >
      <padding-full-customs :config="padding" />
      <margin-full-customs :config="margin" />
    </SidbearSettingsGroup>


    <!-- Modals -->
    <b-modal
      v-if="navbar"
      v-model="linksDialog"
      body-class="nav-modal p-0"
      hide-footer
      hide-header
      size="lg"
    >
      <icon
        class="cursor-pinter ml-3 mt-3"
        icon="close.svg"
        @click.native="dialog(false)"
      />

      <h4 class="h2 text-center my-4">
        Site Navigation
      </h4>

      <b-row
        class="border-top"
        no-gutters
      >
        <template v-if="navbar.items.length">
          <b-col
            class="border-bottom pb-2 pt-3 px-3"
            cols="12"
          >
            <h6 class="font-weight-bold">
              Site Navigation
            </h6>
          </b-col>

          <b-col
            :cols="5"
            class="border-right pb-3"
          >
            <div class="nav-items-els">
              <nested-draggable :children="navbar.items">
                <template
                  slot="item"
                  slot-scope="{page}"
                >
                  <section-item-hover
                    :class="{'active': activeItem ? page.id === activeItem.id : false}"
                    @click.native.stop="activeItem = page"
                  >
                    <icon
                      class="move cursor-move mr-3"
                      icon="move.svg"
                    />
                    <div
                      block
                      class="text-capitalize"
                    >
                      {{ page.item_name }}
                    </div>
                  </section-item-hover>

                  <nested-draggable
                    :children="page.children"
                    :class="{'drop-placeholder': !page.children.length}"
                    :menu="page.id"
                    class="ml-4"
                  >
                    <template
                      slot="item"
                      slot-scope="subPage"
                    >
                      <section-item-hover
                        :class="{'active': activeItem ? subPage.page.id === activeItem.id : false}"
                        @click.native.stop="activeItem = subPage.page"
                      >
                        <icon
                          class="move cursor-move mr-3"
                          icon="move.svg"
                        />
                        <div
                          block
                          class="text-capitalize"
                        >
                          {{ subPage.page.item_name }}
                        </div>
                      </section-item-hover>
                    </template>
                  </nested-draggable>
                </template>
              </nested-draggable>
            </div>

            <div class="px-3 mt-4">
              <b-btn
                class="btn-wide"
                variant="primary"
                @click="dialogNewLink(false)"
              >
                Add Nav Link
              </b-btn>
            </div>
          </b-col>

          <b-col
            v-if="activeItem"
            :cols="7"
            class="d-flex flex-wrap py-3 px-4"
          >
            <div class="w-100">
              <div class="d-flex align-items-center justify-content-between mb-3">
                <h6 class="font-weight-bold text-capitalize">
                  {{ activeItem.title }}
                </h6>
                <icon
                  class="cursor-pinter"
                  icon="trash.svg"
                  @click.native="confirmDialog = true"
                />
              </div>

              <b-form-group label="Name">
                <b-input v-model="activeItem.item_name" />
              </b-form-group>

              <template v-if="!titleOnly">
                <b-form-group
                  v-if="activeItem.item_type === 'page'"
                  label="Page"
                >
                  <div class="position-relative">
                    <v-select
                      v-model="selectedPage"
                      :filterable="false"
                      :options="loadedPages"
                      :reduce="option => option.id"
                      class="custom-select"
                      label="title"
                      @close="onClose"
                      @open="onOpen"
                      @search="onSearch"
                    />
                    <b-spinner
                      v-if="searching || pageIsLoading"
                      class="position-absolute z-index-100 right-3 top-3 bg-white"
                      small
                      variant="primary"
                    />
                  </div>
                </b-form-group>

                <b-form-group
                  v-else-if="activeItem.item_type === 'custom'"
                  label="Custom Link"
                >
                  <b-input v-model="activeItem.item_url" />
                </b-form-group>

                <b-form-group
                  v-else-if="activeItem.item_type === 'scroll-to-element'"
                  label="Scroll To Element"
                >
                  <select-block-page v-model="activeItem.item_url" />
                </b-form-group>
              </template>

              <div
                v-if="activeItem.item_type !== 'scroll-to-element'"
                class="d-flex align-items-center"
              >
                <div
                  v-if="!titleOnly"
                  class="d-flex align-items-center mr-4"
                >
                  <b-form-group>
                    <b-checkbox
                      v-model="activeItem.item_type"
                      class="mt-2"
                      unchecked-value="page"
                      value="custom"
                    >
                      <div class="mt-[-7px]">
                        Custom
                        Link
                      </div>
                    </b-checkbox>
                  </b-form-group>

                  <b-form-group class="ml-4">
                    <b-checkbox
                      v-model="openInNewTab"
                      class="mt-2"
                    >
                      <div class="mt-[-7px]">
                        Open in new tab
                      </div>
                    </b-checkbox>
                  </b-form-group>
                </div>

                <b-form-group v-if="!activeItem.item_parent">
                  <b-checkbox
                    v-model="titleOnly"
                    :disabled="activeItem.children.length > 0"
                    class="mt-2"
                  >
                    <div class="mt-[-7px]">
                      Title only

                      <i
                        v-b-tooltip.hover
                        class="fa fa-question-circle cursor-pointer hover:text-blue-500 ml-1"
                        title="If enabled, sub links support will be added"
                      />
                    </div>
                  </b-checkbox>
                </b-form-group>
              </div>

              <b-form-group class="d-flex">
                <b-btn
                  :disabled="loading"
                  class="btn-wide mr-2"
                  variant="primary"
                  @click="editLink"
                >
                  Save
                </b-btn>
                <b-btn
                  v-if="!activeItem.item_parent && titleOnly"
                  class="btn-wide"
                  variant="outline-primary"
                  @click="dialogNewLink(true)"
                >
                  Add Sub Link
                </b-btn>
              </b-form-group>
            </div>
          </b-col>
        </template>

        <template v-else>
          <b-col
            class="d-flex align-items-center justify-content-center p-5"
            cols="12"
          >
            <b-btn
              class="btn-wide"
              variant="primary"
              @click="dialogNewLink(false)"
            >
              Add Nav Link
            </b-btn>
          </b-col>
        </template>
      </b-row>
    </b-modal>

    <b-modal
      v-if="activeItem"
      v-model="confirmDialog"
      body-class="text-center py-5"
      centered
      hide-footer
      hide-header
      size="lg"
    >
      <h4 class="h3 font-weight-bold my-4">
        Are you sure you want to delete this link?
      </h4>

      <p>
        The link <span class="text-primary">{{ activeItem.title }}</span>
        {{
          activeItem.children ? 'and everything under the links of this category will be removed:' : 'will be removed'
        }}
      </p>

      <div
        v-if="activeItem.children"
        class="d-flex justify-content-center"
      >
        <ul class="mb-4">
          <li
            v-for="(link, index) in activeItem.children"
            :key="index"
          >
            {{ link.item_name }}
          </li>
        </ul>
      </div>


      <div class="d-flex align-items-center justify-content-center">
        <b-btn
          class="btn-wide mr-2"
          variant="danger"
          @click="removeLink"
        >
          Yes
        </b-btn>
        <b-btn
          class="btn-wide"
          @click="confirmDialog = false"
        >
          No
        </b-btn>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ModMixin from '@builder/components/mixins/ModMixin'
import {mapGetters, mapState} from 'vuex'
import TextColor from '@builder/components/editor/controls/TextColor'
import SectionItemHover from '../utils/SectionItemHover'
import NestedDraggable from '../utils/NestedDraggable'
import _ from 'lodash'
import SelectBlockPage from '../controls/SelectBlockPage'
import IconSelect from '../components/IconSelect'
import AlignmentItemsControl from '../controls/AlignmentItemsControl'
import FontSizeControl from '../controls/FontSizeControl'
import PagesPaginationSelectMixin from '@/components/editor/mixins/PagesPaginationSelectMixin'
import SidbearSettingsGroup from '@/components/editor/components/SidbearSettingsGroup'
import FontFamilyAndWeightControl from '@/components/editor/controls/FontFamilyAndWeightControl'
import MultiSlider from '@/components/editor/controls/MultiSlider'
import LinkedControlItem from '@/components/editor/components/linked-control-group/LinkedControlItem'
import PaddingFullCustoms from '@/components/editor/controls/PaddingFullCustoms'
import EventsMixin from '@/components/editor/mixins/EventsMixin'

export default {
  name: 'ButtonMod',
  components: {
    PaddingFullCustoms,
    LinkedControlItem,
    MultiSlider,
    FontFamilyAndWeightControl,
    SidbearSettingsGroup,
    FontSizeControl,
    AlignmentItemsControl,
    IconSelect, SelectBlockPage, NestedDraggable, SectionItemHover, TextColor
  },
  mixins: [ModMixin, PagesPaginationSelectMixin, EventsMixin],
  data () {
    return {
      icons: ['', 'fa fa-500px', 'fa fa-address-book', 'fa fa-address-book-o', 'fa fa-address-card', 'fa fa-address-card-o', 'fa fa-adjust', 'fa fa-adn', 'fa fa-align-center', 'fa fa-align-justify', 'fa fa-align-left', 'fa fa-align-right', 'fa fa-amazon', 'fa fa-ambulance', 'fa fa-american-sign-language-interpreting', 'fa fa-anchor', 'fa fa-android', 'fa fa-angellist', 'fa fa-angle-double-down', 'fa fa-angle-double-left', 'fa fa-angle-double-right', 'fa fa-angle-double-up', 'fa fa-angle-down', 'fa fa-angle-left', 'fa fa-angle-right', 'fa fa-angle-up', 'fa fa-apple', 'fa fa-archive', 'fa fa-area-chart', 'fa fa-arrow-circle-down', 'fa fa-arrow-circle-left', 'fa fa-arrow-circle-o-down', 'fa fa-arrow-circle-o-left', 'fa fa-arrow-circle-o-right', 'fa fa-arrow-circle-o-up', 'fa fa-arrow-circle-right', 'fa fa-arrow-circle-up', 'fa fa-arrow-down', 'fa fa-arrow-left', 'fa fa-arrow-right', 'fa fa-arrow-up', 'fa fa-arrows', 'fa fa-arrows-alt', 'fa fa-arrows-h', 'fa fa-arrows-v', 'fa fa-asl-interpreting', 'fa fa-assistive-listening-systems', 'fa fa-asterisk', 'fa fa-at', 'fa fa-audio-description', 'fa fa-automobile', 'fa fa-backward', 'fa fa-balance-scale', 'fa fa-ban', 'fa fa-bandcamp', 'fa fa-bank', 'fa fa-bar-chart', 'fa fa-bar-chart-o', 'fa fa-barcode', 'fa fa-bars', 'fa fa-bath', 'fa fa-bathtub', 'fa fa-battery', 'fa fa-battery-0', 'fa fa-battery-1', 'fa fa-battery-2', 'fa fa-battery-3', 'fa fa-battery-4', 'fa fa-battery-empty', 'fa fa-battery-full', 'fa fa-battery-half', 'fa fa-battery-quarter', 'fa fa-battery-three-quarters', 'fa fa-bed', 'fa fa-beer', 'fa fa-behance', 'fa fa-behance-square', 'fa fa-bell', 'fa fa-bell-o', 'fa fa-bell-slash', 'fa fa-bell-slash-o', 'fa fa-bicycle', 'fa fa-binoculars', 'fa fa-birthday-cake', 'fa fa-bitbucket', 'fa fa-bitbucket-square', 'fa fa-bitcoin', 'fa fa-black-tie', 'fa fa-blind', 'fa fa-bluetooth', 'fa fa-bluetooth-b', 'fa fa-bold', 'fa fa-bolt', 'fa fa-bomb', 'fa fa-book', 'fa fa-bookmark', 'fa fa-bookmark-o', 'fa fa-braille', 'fa fa-briefcase', 'fa fa-btc', 'fa fa-bug', 'fa fa-building', 'fa fa-building-o', 'fa fa-bullhorn', 'fa fa-bullseye', 'fa fa-bus', 'fa fa-buysellads', 'fa fa-cab', 'fa fa-calculator', 'fa fa-calendar', 'fa fa-calendar-check-o', 'fa fa-calendar-minus-o', 'fa fa-calendar-o', 'fa fa-calendar-plus-o', 'fa fa-calendar-times-o', 'fa fa-camera', 'fa fa-camera-retro', 'fa fa-car', 'fa fa-caret-down', 'fa fa-caret-left', 'fa fa-caret-right', 'fa fa-caret-square-o-down', 'fa fa-caret-square-o-left', 'fa fa-caret-square-o-right', 'fa fa-caret-square-o-up', 'fa fa-caret-up', 'fa fa-cart-arrow-down', 'fa fa-cart-plus', 'fa fa-cc', 'fa fa-cc-amex', 'fa fa-cc-diners-club', 'fa fa-cc-discover', 'fa fa-cc-jcb', 'fa fa-cc-mastercard', 'fa fa-cc-paypal', 'fa fa-cc-stripe', 'fa fa-cc-visa', 'fa fa-certificate', 'fa fa-chain', 'fa fa-chain-broken', 'fa fa-check', 'fa fa-check-circle', 'fa fa-check-circle-o', 'fa fa-check-square', 'fa fa-check-square-o', 'fa fa-chevron-circle-down', 'fa fa-chevron-circle-left', 'fa fa-chevron-circle-right', 'fa fa-chevron-circle-up', 'fa fa-chevron-down', 'fa fa-chevron-left', 'fa fa-chevron-right', 'fa fa-chevron-up', 'fa fa-child', 'fa fa-chrome', 'fa fa-circle', 'fa fa-circle-o', 'fa fa-circle-o-notch', 'fa fa-circle-thin', 'fa fa-clipboard', 'fa fa-clock-o', 'fa fa-clone', 'fa fa-close', 'fa fa-cloud', 'fa fa-cloud-download', 'fa fa-cloud-upload', 'fa fa-cny', 'fa fa-code', 'fa fa-code-fork', 'fa fa-codepen', 'fa fa-codiepie', 'fa fa-coffee', 'fa fa-cog', 'fa fa-cogs', 'fa fa-columns', 'fa fa-comment', 'fa fa-comment-o', 'fa fa-commenting', 'fa fa-commenting-o', 'fa fa-comments', 'fa fa-comments-o', 'fa fa-compass', 'fa fa-compress', 'fa fa-connectdevelop', 'fa fa-contao', 'fa fa-copy', 'fa fa-copyright', 'fa fa-creative-commons', 'fa fa-credit-card', 'fa fa-credit-card-alt', 'fa fa-crop', 'fa fa-crosshairs', 'fa fa-css3', 'fa fa-cube', 'fa fa-cubes', 'fa fa-cut', 'fa fa-cutlery', 'fa fa-dashboard', 'fa fa-dashcube', 'fa fa-database', 'fa fa-deaf', 'fa fa-deafness', 'fa fa-dedent', 'fa fa-delicious', 'fa fa-desktop', 'fa fa-deviantart', 'fa fa-diamond', 'fa fa-digg', 'fa fa-dollar', 'fa fa-dot-circle-o', 'fa fa-download', 'fa fa-dribbble', 'fa fa-drivers-license', 'fa fa-drivers-license-o', 'fa fa-dropbox', 'fa fa-drupal', 'fa fa-edge', 'fa fa-edit', 'fa fa-eercast', 'fa fa-eject', 'fa fa-ellipsis-h', 'fa fa-ellipsis-v', 'fa fa-empire', 'fa fa-envelope', 'fa fa-envelope-o', 'fa fa-envelope-open', 'fa fa-envelope-open-o', 'fa fa-envelope-square', 'fa fa-envira', 'fa fa-eraser', 'fa fa-etsy', 'fa fa-eur', 'fa fa-euro', 'fa fa-exchange', 'fa fa-exclamation', 'fa fa-exclamation-circle', 'fa fa-exclamation-triangle', 'fa fa-expand', 'fa fa-expeditedssl', 'fa fa-external-link', 'fa fa-external-link-square', 'fa fa-eye', 'fa fa-eye-slash', 'fa fa-eyedropper', 'fa fa-fa', 'fa fa-facebook', 'fa fa-facebook-f', 'fa fa-facebook-official', 'fa fa-facebook-square', 'fa fa-fast-backward', 'fa fa-fast-forward', 'fa fa-fax', 'fa fa-feed', 'fa fa-female', 'fa fa-fighter-jet', 'fa fa-file', 'fa fa-file-archive-o', 'fa fa-file-audio-o', 'fa fa-file-code-o', 'fa fa-file-excel-o', 'fa fa-file-image-o', 'fa fa-file-movie-o', 'fa fa-file-o', 'fa fa-file-pdf-o', 'fa fa-file-photo-o', 'fa fa-file-picture-o', 'fa fa-file-powerpoint-o', 'fa fa-file-sound-o', 'fa fa-file-text', 'fa fa-file-text-o', 'fa fa-file-video-o', 'fa fa-file-word-o', 'fa fa-file-zip-o', 'fa fa-files-o', 'fa fa-film', 'fa fa-filter', 'fa fa-fire', 'fa fa-fire-extinguisher', 'fa fa-firefox', 'fa fa-first-order', 'fa fa-flag', 'fa fa-flag-checkered', 'fa fa-flag-o', 'fa fa-flash', 'fa fa-flask', 'fa fa-flickr', 'fa fa-floppy-o', 'fa fa-folder', 'fa fa-folder-o', 'fa fa-folder-open', 'fa fa-folder-open-o', 'fa fa-font', 'fa fa-font-awesome', 'fa fa-fonticons', 'fa fa-fort-awesome', 'fa fa-forumbee', 'fa fa-forward', 'fa fa-foursquare', 'fa fa-free-code-camp', 'fa fa-frown-o', 'fa fa-futbol-o', 'fa fa-gamepad', 'fa fa-gavel', 'fa fa-gbp', 'fa fa-ge', 'fa fa-gear', 'fa fa-gears', 'fa fa-genderless', 'fa fa-get-pocket', 'fa fa-gg', 'fa fa-gg-circle', 'fa fa-gift', 'fa fa-git', 'fa fa-git-square', 'fa fa-github', 'fa fa-github-alt', 'fa fa-github-square', 'fa fa-gitlab', 'fa fa-gittip', 'fa fa-glass', 'fa fa-glide', 'fa fa-glide-g', 'fa fa-globe', 'fa fa-google', 'fa fa-google-plus', 'fa fa-google-plus-circle', 'fa fa-google-plus-official', 'fa fa-google-plus-square', 'fa fa-google-wallet', 'fa fa-graduation-cap', 'fa fa-gratipay', 'fa fa-grav', 'fa fa-group', 'fa fa-h-square', 'fa fa-hacker-news', 'fa fa-hand-grab-o', 'fa fa-hand-lizard-o', 'fa fa-hand-o-down', 'fa fa-hand-o-left', 'fa fa-hand-o-right', 'fa fa-hand-o-up', 'fa fa-hand-paper-o', 'fa fa-hand-peace-o', 'fa fa-hand-pointer-o', 'fa fa-hand-rock-o', 'fa fa-hand-scissors-o', 'fa fa-hand-spock-o', 'fa fa-hand-stop-o', 'fa fa-handshake-o', 'fa fa-hard-of-hearing', 'fa fa-hashtag', 'fa fa-hdd-o', 'fa fa-header', 'fa fa-headphones', 'fa fa-heart', 'fa fa-heart-o', 'fa fa-heartbeat', 'fa fa-history', 'fa fa-home', 'fa fa-hospital-o', 'fa fa-hotel', 'fa fa-hourglass', 'fa fa-hourglass-1', 'fa fa-hourglass-2', 'fa fa-hourglass-3', 'fa fa-hourglass-end', 'fa fa-hourglass-half', 'fa fa-hourglass-o', 'fa fa-hourglass-start', 'fa fa-houzz', 'fa fa-html5', 'fa fa-i-cursor', 'fa fa-id-badge', 'fa fa-id-card', 'fa fa-id-card-o', 'fa fa-ils', 'fa fa-image', 'fa fa-imdb', 'fa fa-inbox', 'fa fa-indent', 'fa fa-industry', 'fa fa-info', 'fa fa-info-circle', 'fa fa-inr', 'fa fa-instagram', 'fa fa-institution', 'fa fa-internet-explorer', 'fa fa-intersex', 'fa fa-ioxhost', 'fa fa-italic', 'fa fa-joomla', 'fa fa-jpy', 'fa fa-jsfiddle', 'fa fa-key', 'fa fa-keyboard-o', 'fa fa-krw', 'fa fa-language', 'fa fa-laptop', 'fa fa-lastfm', 'fa fa-lastfm-square', 'fa fa-leaf', 'fa fa-leanpub', 'fa fa-legal', 'fa fa-lemon-o', 'fa fa-level-down', 'fa fa-level-up', 'fa fa-life-bouy', 'fa fa-life-buoy', 'fa fa-life-ring', 'fa fa-life-saver', 'fa fa-lightbulb-o', 'fa fa-line-chart', 'fa fa-link', 'fa fa-linkedin', 'fa fa-linkedin-square', 'fa fa-linode', 'fa fa-linux', 'fa fa-list', 'fa fa-list-alt', 'fa fa-list-ol', 'fa fa-list-ul', 'fa fa-location-arrow', 'fa fa-lock', 'fa fa-long-arrow-down', 'fa fa-long-arrow-left', 'fa fa-long-arrow-right', 'fa fa-long-arrow-up', 'fa fa-low-vision', 'fa fa-magic', 'fa fa-magnet', 'fa fa-mail-forward', 'fa fa-mail-reply', 'fa fa-mail-reply-all', 'fa fa-male', 'fa fa-map', 'fa fa-map-marker', 'fa fa-map-o', 'fa fa-map-pin', 'fa fa-map-signs', 'fa fa-mars', 'fa fa-mars-double', 'fa fa-mars-stroke', 'fa fa-mars-stroke-h', 'fa fa-mars-stroke-v', 'fa fa-maxcdn', 'fa fa-meanpath', 'fa fa-medium', 'fa fa-medkit', 'fa fa-meetup', 'fa fa-meh-o', 'fa fa-mercury', 'fa fa-microchip', 'fa fa-microphone', 'fa fa-microphone-slash', 'fa fa-minus', 'fa fa-minus-circle', 'fa fa-minus-square', 'fa fa-minus-square-o', 'fa fa-mixcloud', 'fa fa-mobile', 'fa fa-mobile-phone', 'fa fa-modx', 'fa fa-money', 'fa fa-moon-o', 'fa fa-mortar-board', 'fa fa-motorcycle', 'fa fa-mouse-pointer', 'fa fa-music', 'fa fa-navicon', 'fa fa-neuter', 'fa fa-newspaper-o', 'fa fa-object-group', 'fa fa-object-ungroup', 'fa fa-odnoklassniki', 'fa fa-odnoklassniki-square', 'fa fa-opencart', 'fa fa-openid', 'fa fa-opera', 'fa fa-optin-monster', 'fa fa-outdent', 'fa fa-pagelines', 'fa fa-paint-brush', 'fa fa-paper-plane', 'fa fa-paper-plane-o', 'fa fa-paperclip', 'fa fa-paragraph', 'fa fa-paste', 'fa fa-pause', 'fa fa-pause-circle', 'fa fa-pause-circle-o', 'fa fa-paw', 'fa fa-paypal', 'fa fa-pencil', 'fa fa-pencil-square', 'fa fa-pencil-square-o', 'fa fa-percent', 'fa fa-phone', 'fa fa-phone-square', 'fa fa-photo', 'fa fa-picture-o', 'fa fa-pie-chart', 'fa fa-pied-piper', 'fa fa-pied-piper-alt', 'fa fa-pied-piper-pp', 'fa fa-pinterest', 'fa fa-pinterest-p', 'fa fa-pinterest-square', 'fa fa-plane', 'fa fa-play', 'fa fa-play-circle', 'fa fa-play-circle-o', 'fa fa-plug', 'fa fa-plus', 'fa fa-plus-circle', 'fa fa-plus-square', 'fa fa-plus-square-o', 'fa fa-podcast', 'fa fa-power-off', 'fa fa-print', 'fa fa-product-hunt', 'fa fa-puzzle-piece', 'fa fa-qq', 'fa fa-qrcode', 'fa fa-question', 'fa fa-question-circle', 'fa fa-question-circle-o', 'fa fa-quora', 'fa fa-quote-left', 'fa fa-quote-right', 'fa fa-ra', 'fa fa-random', 'fa fa-ravelry', 'fa fa-rebel', 'fa fa-recycle', 'fa fa-reddit', 'fa fa-reddit-alien', 'fa fa-reddit-square', 'fa fa-refresh', 'fa fa-registered', 'fa fa-remove', 'fa fa-renren', 'fa fa-reorder', 'fa fa-repeat', 'fa fa-reply', 'fa fa-reply-all', 'fa fa-resistance', 'fa fa-retweet', 'fa fa-rmb', 'fa fa-road', 'fa fa-rocket', 'fa fa-rotate-left', 'fa fa-rotate-right', 'fa fa-rouble', 'fa fa-rss', 'fa fa-rss-square', 'fa fa-rub', 'fa fa-ruble', 'fa fa-rupee', 'fa fa-s15', 'fa fa-safari', 'fa fa-save', 'fa fa-scissors', 'fa fa-scribd', 'fa fa-search', 'fa fa-search-minus', 'fa fa-search-plus', 'fa fa-sellsy', 'fa fa-send', 'fa fa-send-o', 'fa fa-server', 'fa fa-share', 'fa fa-share-alt', 'fa fa-share-alt-square', 'fa fa-share-square', 'fa fa-share-square-o', 'fa fa-shekel', 'fa fa-sheqel', 'fa fa-shield', 'fa fa-ship', 'fa fa-shirtsinbulk', 'fa fa-shopping-bag', 'fa fa-shopping-basket', 'fa fa-shopping-cart', 'fa fa-shower', 'fa fa-sign-in', 'fa fa-sign-language', 'fa fa-sign-out', 'fa fa-signal', 'fa fa-signing', 'fa fa-simplybuilt', 'fa fa-sitemap', 'fa fa-skyatlas', 'fa fa-skype', 'fa fa-slack', 'fa fa-sliders', 'fa fa-slideshare', 'fa fa-smile-o', 'fa fa-snapchat', 'fa fa-snapchat-ghost', 'fa fa-snapchat-square', 'fa fa-snowflake-o', 'fa fa-soccer-ball-o', 'fa fa-sort', 'fa fa-sort-alpha-asc', 'fa fa-sort-alpha-desc', 'fa fa-sort-amount-asc', 'fa fa-sort-amount-desc', 'fa fa-sort-asc', 'fa fa-sort-desc', 'fa fa-sort-down', 'fa fa-sort-numeric-asc', 'fa fa-sort-numeric-desc', 'fa fa-sort-up', 'fa fa-soundcloud', 'fa fa-space-shuttle', 'fa fa-spinner', 'fa fa-spoon', 'fa fa-spotify', 'fa fa-square', 'fa fa-square-o', 'fa fa-stack-exchange', 'fa fa-stack-overflow', 'fa fa-star', 'fa fa-star-half', 'fa fa-star-half-empty', 'fa fa-star-half-full', 'fa fa-star-half-o', 'fa fa-star-o', 'fa fa-steam', 'fa fa-steam-square', 'fa fa-step-backward', 'fa fa-step-forward', 'fa fa-stethoscope', 'fa fa-sticky-note', 'fa fa-sticky-note-o', 'fa fa-stop', 'fa fa-stop-circle', 'fa fa-stop-circle-o', 'fa fa-street-view', 'fa fa-strikethrough', 'fa fa-stumbleupon', 'fa fa-stumbleupon-circle', 'fa fa-subscript', 'fa fa-subway', 'fa fa-suitcase', 'fa fa-sun-o', 'fa fa-superpowers', 'fa fa-superscript', 'fa fa-support', 'fa fa-table', 'fa fa-tablet', 'fa fa-tachometer', 'fa fa-tag', 'fa fa-tags', 'fa fa-tasks', 'fa fa-taxi', 'fa fa-telegram', 'fa fa-television', 'fa fa-tencent-weibo', 'fa fa-terminal', 'fa fa-text-height', 'fa fa-text-width', 'fa fa-th', 'fa fa-th-large', 'fa fa-th-list', 'fa fa-themeisle', 'fa fa-thermometer', 'fa fa-thermometer-0', 'fa fa-thermometer-1', 'fa fa-thermometer-2', 'fa fa-thermometer-3', 'fa fa-thermometer-4', 'fa fa-thermometer-empty', 'fa fa-thermometer-full', 'fa fa-thermometer-half', 'fa fa-thermometer-quarter', 'fa fa-thermometer-three-quarters', 'fa fa-thumb-tack', 'fa fa-thumbs-down', 'fa fa-thumbs-o-down', 'fa fa-thumbs-o-up', 'fa fa-thumbs-up', 'fa fa-ticket', 'fa fa-times', 'fa fa-times-circle', 'fa fa-times-circle-o', 'fa fa-times-rectangle', 'fa fa-times-rectangle-o', 'fa fa-tint', 'fa fa-toggle-down', 'fa fa-toggle-left', 'fa fa-toggle-off', 'fa fa-toggle-on', 'fa fa-toggle-right', 'fa fa-toggle-up', 'fa fa-trademark', 'fa fa-train', 'fa fa-transgender', 'fa fa-transgender-alt', 'fa fa-trash', 'fa fa-trash-o', 'fa fa-tree', 'fa fa-trello', 'fa fa-tripadvisor', 'fa fa-trophy', 'fa fa-truck', 'fa fa-try', 'fa fa-tty', 'fa fa-tumblr', 'fa fa-tumblr-square', 'fa fa-turkish-lira', 'fa fa-tv', 'fa fa-twitch', 'fa fa-twitter', 'fa fa-twitter-square', 'fa fa-umbrella', 'fa fa-underline', 'fa fa-undo', 'fa fa-universal-access', 'fa fa-university', 'fa fa-unlink', 'fa fa-unlock', 'fa fa-unlock-alt', 'fa fa-unsorted', 'fa fa-upload', 'fa fa-usb', 'fa fa-usd', 'fa fa-user', 'fa fa-user-circle', 'fa fa-user-circle-o', 'fa fa-user-md', 'fa fa-user-o', 'fa fa-user-plus', 'fa fa-user-secret', 'fa fa-user-times', 'fa fa-users', 'fa fa-vcard', 'fa fa-vcard-o', 'fa fa-venus', 'fa fa-venus-double', 'fa fa-venus-mars', 'fa fa-viacoin', 'fa fa-viadeo', 'fa fa-viadeo-square', 'fa fa-video-camera', 'fa fa-vimeo', 'fa fa-vimeo-square', 'fa fa-vine', 'fa fa-vk', 'fa fa-volume-control-phone', 'fa fa-volume-down', 'fa fa-volume-off', 'fa fa-volume-up', 'fa fa-warning', 'fa fa-wechat', 'fa fa-weibo', 'fa fa-weixin', 'fa fa-whatsapp', 'fa fa-wheelchair', 'fa fa-wheelchair-alt', 'fa fa-wifi', 'fa fa-wikipedia-w', 'fa fa-window-close', 'fa fa-window-close-o', 'fa fa-window-maximize', 'fa fa-window-minimize', 'fa fa-window-restore', 'fa fa-windows', 'fa fa-won', 'fa fa-wordpress', 'fa fa-wpbeginner', 'fa fa-wpexplorer', 'fa fa-wpforms', 'fa fa-wrench', 'fa fa-xing', 'fa fa-xing-square', 'fa fa-y-combinator', 'fa fa-y-combinator-square', 'fa fa-yahoo', 'fa fa-yc', 'fa fa-yc-square', 'fa fa-yelp', 'fa fa-yen', 'fa fa-yoast', 'fa fa-youtube', 'fa fa-youtube-play', 'fa fa-youtube-square'],
      linksDialog: false,
      confirmDialog: false,
      activeItem: null,
      loading: false,
      titleOnly: false,
      shapeOptions: [
        {
          key: '6px',
          label: 'Rounded'
        },
        {
          key: '0',
          label: 'Squared'
        },
        {
          key: '40px',
          label: 'Pilled'
        }
      ],
      onHoverOptions: [
        {
          label: 'None',
          value: null
        },
        {
          label: 'Background',
          value: 'background'
        },
        {
          label: 'Underline',
          value: 'underline'
        },
        {
          label: 'Transform',
          value: 'transform'
        }
      ],
      activeLinkStyleOptions: [
        {
          label: 'Color',
          value: 'color'
        },
        {
          label: 'Background',
          value: 'background'
        },
        {
          label: 'Underline',
          value: 'underline'
        }
      ]
    }
  },
  computed: {
    selectedPage: {
      get () {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.paginationSelectedPage = this.activeItem.item_object_id
        this.loadPageState()
        return this.activeItem.item_object_id
      },
      set (val) {
        this.activeItem.item_object_id = val
      }
    },
    ...mapGetters('pages', {
      pages: 'getPages'
    }),
    onHover: {
      get () {
        return this.config.onHover || null
      },
      set (val) {
        this.$set(this.config, 'onHover', val)
      }
    },
    spacing: {
      get () {
        return this.config.spacing || 8
      },
      set (val) {
        this.$set(this.config, 'spacing', val)
      }
    },
    padding () {
      if (!this.config.padding) {
        this.$set(this.config, 'padding', {})
      }

      return this.config.padding
    },
    margin () {
      if (!this.config.margin) {
        this.$set(this.config, 'margin', {})
      }

      return this.config.margin
    },
    linkHoverBackground: {
      get () {
        return this.config.linkHoverBackground || '#cdcdcd'
      },
      set (val) {
        this.$set(this.config, 'linkHoverBackground', val)
      }
    },
    linkHoverBackgroundShape: {
      get () {
        return this.config.linkHoverBackgroundShape || '0'
      },
      set (val) {
        this.$set(this.config, 'linkHoverBackgroundShape', val)
      }
    },
    wrapLinks: {
      get () {
        return this.config.wrapLinks || false
      },
      set (val) {
        this.$set(this.config, 'wrapLinks', val)
      }
    },
    fullWidthMenu: {
      get () {
        return _.get(this.config, ['fullWidthMenu', this.resolutioner], false)
      },
      set (val) {
        this.$set(this.config, 'fullWidthMenu', {..._.set(this.config.fullWidthMenu || {}, [this.resolutioner], val)})
      }
    },
    linksColor: {
      get () {
        return this.config.linksColor || this.globalStyles.colors.primary
      },
      set (val) {
        this.$set(this.config, 'linksColor', val)
      }
    },
    linkHoverColor: {
      get () {
        return this.config.linkHoverColor || '#C5C5C5B3'
      },
      set (val) {
        this.$set(this.config, 'linkHoverColor', val)
      }
    },
    activeLinkStyle: {
      get () {
        return this.config.activeLinkStyle || 'color'
      },
      set (val) {
        this.$set(this.config, 'activeLinkStyle', val)
      }
    },
    children () {
      return this.activeItem.children
    },
    iconOpen: {
      get () {
        return _.get(this.config, 'hamburgerIcon_open', 'fa fa-bars')
      },
      set (val) {
        this.$set(this.config, 'hamburgerIcon_open', val)
      }
    },
    shape: {
      get () {
        return _.get(this.config, 'shape', '6px')
      },
      set (val) {
        this.$set(this.config, 'shape', val)
      }
    },
    fontWeight: {
      get () {
        return _.get(this.config, 'fontWeight', '400')
      },
      set (val) {
        this.$set(this.config, 'fontWeight', val)
      }
    },
    fontFamily: {
      get () {
        return _.get(this.config, 'fontFamily', 'Roboto')
      },
      set (val) {
        this.$set(this.config, 'fontFamily', val)
      }
    },
    activeBackgroundColor: {
      get () {
        return _.get(this.config, 'activeBackgroundColor', 'rgba(225, 225, 225, 0)')
      },
      set (val) {
        this.$set(this.config, 'activeBackgroundColor', val)
      }
    },
    iconClose: {
      get () {
        return _.get(this.config, 'hamburgerIcon_close', 'fa fa-times')
      },
      set (val) {
        this.$set(this.config, 'hamburgerIcon_close', val)
      }
    },
    iconColor: {
      get () {
        return _.get(this.section.options.content.menuItems, 'hamburgerIcon_color', '#656565')
      },
      set (val) {
        this.$set(this.config, 'hamburgerIcon_color', val)
      }
    },
    closeIconColor: {
      get () {
        return _.get(this.section.options.content.menuItems, 'hamburgerIcon_close_color', this.iconColor)
      },
      set (val) {
        this.$set(this.config, 'hamburgerIcon_close_color', val)
      }
    },
    dropdownColor: {
      get () {
        return _.get(this.config, 'dropdownColor', '#fff')
      },
      set (val) {
        this.$set(this.config, 'dropdownColor', val)
      }
    },
    menuWidth: {
      get () {
        return this.config.menuWidth || {}
      },
      set (val) {
        this.$set(this.config, 'menuWidth', val)
      }
    },
    dropdownTextColor: {
      get () {
        return _.get(this.config, 'dropdownTextColor', this.linksColor)
      },
      set (val) {
        this.$set(this.config, 'dropdownTextColor', val)
      }
    },
    showButton: {
      get () {
        return _.get(this.config, 'showButton', true)
      },
      set (val) {
        this.$set(this.config, 'showButton', val)
      }
    },
    bringToFront: {
      get () {
        return _.get(this.config, ['bringToFront', this.resolutioner], false)
      },
      set (val) {
        this.$set(this.config, 'bringToFront', {..._.set(this.config.bringToFront || {}, [this.resolutioner], val)})
      }
    },
    ...mapGetters('pages', [
      'getPages'
    ]),
    ...mapState('menu', {
      navbar: state => state.navbarMenu
    }),
    openInNewTab: {
      get () {
        return !!_.get(this.activeItem, 'open_in_new_tab', false)
      },
      set (val) {
        this.$set(this.activeItem, 'open_in_new_tab', val)
      }
    }
  },
  watch: {
    activeItem: {
      deep: true,
      handler: function (val) {
        this.titleOnly = false
        if (val && val.children.length) {
          this.titleOnly = true
        }
      }
    }
  },
  mounted () {
    this.listen('add-navigation-menu-close', () => this.dialog(true))
  },
  methods: {
    editPage () {
      VEvent.fire('newPageModal', this.activeItem.page.key)
      this.linksDialog = false
    },
    dialog (state) {
      if (this.navbar.items.length) {
        return this.linksDialog = state
      }

      this.dialogNewLink()
    },
    editLink () {
      this.loading = true
      this.$store.dispatch('menu/editLink', this.activeItem)
        .finally(() => {
          VEvent.fire('alt-loader', {
            state: false,
            message: 'Saved.'
          })
          this.loading = false
        })
    },
    removeLink () {
      this.$store.dispatch('menu/deleteNavbarItem', this.activeItem)
      this.activeItem = null

      this.dialog(true)
      this.confirmDialog = false
    },
    dialogNewLink (state) {
      this.linksDialog = false
      VEvent.fire('newPageDialog', this.activeItem && state ? this.activeItem : null)
    }
  }
}
</script>

<style lang="scss">
.nav-modal {
  .nav-items-els {
    height: 541px;
    overflow-y: auto;
  }

  .editor-toolbar-collapse-header {
    padding: 12px 19px;
    cursor: pointer;
    font-size: 16px;
    background-color: #fff;
    color: #44474A;

    &:hover,
    &.active {
      background-color: #FAFCFF;
      color: #4F83E3;
    }
  }
}
</style>
